import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TaskProvider } from "@/contexts/TaskContext";
import { ExamModeProvider } from "@/contexts/ExamModeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";

import AppShell from "@/components/AppShell";
import PageSkeleton from "@/components/PageSkeleton";
import RouteSpinner from "@/components/RouteSpinner";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy-loaded routes – keeps initial bundle small
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AuthAdmin = lazy(() => import("./pages/AuthAdmin"));
const Blog = lazy(() => import("./pages/Blog"));
const TaskCollections = lazy(() => import("./pages/TaskCollections"));
const BazaWiedzy = lazy(() => import("./pages/BazaWiedzy"));
const Community = lazy(() => import("./pages/Community"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const UserPanel = lazy(() => import("./pages/UserPanel"));
const About = lazy(() => import("./pages/About"));
const Sklepik = lazy(() => import("./pages/Sklepik"));
const Challenge = lazy(() => import("./pages/Challenge"));
const Klasa = lazy(() => import("./pages/Klasa"));
const Sandbox = lazy(() => import("./pages/Sandbox"));
const Start = lazy(() => import("./pages/Start"));
const Regulamin = lazy(() => import("./pages/Regulamin"));
const PolitykaPrywatnosci = lazy(() => import("./pages/PolitykaPrywatnosci"));
const SharedResult = lazy(() => import("./pages/SharedResult"));
const OgDebug = lazy(() => import("./pages/OgDebug"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" storageKey="biozadania-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <TooltipProvider>
        <BrowserRouter>
          <Routes>
              {/* Auth-admin: completely isolated, no providers */}
              <Route path="/auth-admin" element={
                <>
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<RouteSpinner />}>
                    <AuthAdmin />
                  </Suspense>
                </>
              } />

              {/* Single AppShell for ALL routes — header and background never remount */}
              <Route element={<AppShell />}>
                {/* Light pages — no TaskProvider needed */}
                <Route path="/blog" element={<Suspense fallback={<RouteSpinner />}><Blog /></Suspense>} />
                <Route path="/blog/:slug" element={<Suspense fallback={<RouteSpinner />}><Blog /></Suspense>} />
                <Route path="/baza-wiedzy" element={<Suspense fallback={<RouteSpinner />}><BazaWiedzy /></Suspense>} />
                <Route path="/spolecznosc" element={<Suspense fallback={<RouteSpinner />}><Community /></Suspense>} />
                <Route path="/roadmap" element={<Suspense fallback={<RouteSpinner />}><Roadmap /></Suspense>} />
                <Route path="/panel" element={<Suspense fallback={<RouteSpinner />}><UserPanel /></Suspense>} />
                <Route path="/klasa" element={<Suspense fallback={<RouteSpinner />}><Klasa /></Suspense>} />
                <Route path="/o-mnie" element={<Suspense fallback={<RouteSpinner />}><About /></Suspense>} />
                <Route path="/sklepik" element={<Suspense fallback={<RouteSpinner />}><Sklepik /></Suspense>} />
                <Route path="/auth" element={<Suspense fallback={<RouteSpinner />}><Auth /></Suspense>} />
                <Route path="/reset-password" element={<Suspense fallback={<RouteSpinner />}><ResetPassword /></Suspense>} />
                <Route path="/sandbox" element={<Suspense fallback={<RouteSpinner />}><Sandbox /></Suspense>} />
                <Route path="/start" element={<Suspense fallback={<RouteSpinner />}><Start /></Suspense>} />
                <Route path="/regulamin" element={<Suspense fallback={<RouteSpinner />}><Regulamin /></Suspense>} />
                <Route path="/polityka-prywatnosci" element={<Suspense fallback={<RouteSpinner />}><PolitykaPrywatnosci /></Suspense>} />
                <Route path="/wynik/:slug" element={<Suspense fallback={<RouteSpinner />}><SharedResult /></Suspense>} />
                <Route path="/og-debug" element={<Suspense fallback={<RouteSpinner />}><OgDebug /></Suspense>} />

                {/* Task pages + Admin: wrapped with TaskProvider/ExamModeProvider per-route */}
                <Route path="/" element={
                  <TaskProvider>
                    <ExamModeProvider>
                      <Suspense fallback={<PageSkeleton />}>
                        <Index />
                      </Suspense>
                    </ExamModeProvider>
                  </TaskProvider>
                } />
                <Route path="/zadanie/:taskId" element={
                  <TaskProvider>
                    <ExamModeProvider>
                      <Suspense fallback={<PageSkeleton />}>
                        <Index />
                      </Suspense>
                    </ExamModeProvider>
                  </TaskProvider>
                } />
                {["/arkusz/:sheetSlug", "/dzial/:categorySlug"].map((p) => (
                  <Route key={p} path={p} element={
                    <TaskProvider>
                      <ExamModeProvider>
                        <Suspense fallback={<PageSkeleton />}>
                          <Index />
                        </Suspense>
                      </ExamModeProvider>
                    </TaskProvider>
                  } />
                ))}
                <Route path="/zadania" element={<Suspense fallback={<RouteSpinner />}><TaskCollections /></Suspense>} />

                <Route path="/wyzwanie/:challengeId" element={
                  <TaskProvider>
                    <ExamModeProvider>
                      <Suspense fallback={<PageSkeleton />}>
                        <Challenge />
                      </Suspense>
                    </ExamModeProvider>
                  </TaskProvider>
                } />
                <Route path="/admin" element={
                  <TaskProvider>
                    <ExamModeProvider>
                      <Suspense fallback={<RouteSpinner />}>
                        <Admin />
                      </Suspense>
                    </ExamModeProvider>
                  </TaskProvider>
                } />
                <Route path="/admin/blog" element={<Suspense fallback={<RouteSpinner />}><AdminBlog /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<RouteSpinner />}><NotFound /></Suspense>} />
              </Route>
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
